import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../services/api';
import api from '../config/api';
import auth from '../utils/auth';
import { AuthResponse } from '../types';
import { registerFCMTokenWithBackend } from '../config/firebase';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState<{ message: string } | null>(null);
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [forgotStep, setForgotStep] = useState<'email' | 'otp' | 'reset'>('email');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotError, setForgotError] = useState('');
  const [forgotSuccess, setForgotSuccess] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4500);
    return () => clearTimeout(t);
  }, [toast]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axiosInstance.post<AuthResponse>(api.auth.login, {
        email,
        password,
      });

      auth.setToken(response.data.token);
      auth.setUser(response.data.user);
      navigate('/dashboard');
      registerFCMTokenWithBackend(auth.getAuthHeader, api.notifications.register).catch(() => {});
    } catch (err: any) {
      const code = err.response?.data?.code;
      const msg =
        code === 'invalid_email'
          ? 'البريد الإلكتروني غير صحيح'
          : code === 'invalid_password'
            ? 'كلمة المرور غير صحيحة'
            : err.response?.data?.error || 'فشل تسجيل الدخول';
      setError(msg);
      setToast({ message: msg });
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPasswordEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    setForgotError('');
    setForgotSuccess('');
    setForgotLoading(true);

    try {
      await axiosInstance.post(api.auth.forgotPassword, { email: forgotEmail });
      setForgotSuccess('تم إرسال رمز التحقق إلى بريدك الإلكتروني');
      setForgotStep('otp');
    } catch (err: any) {
      setForgotError(err.response?.data?.error || 'حدث خطأ أثناء إرسال رمز التحقق');
    } finally {
      setForgotLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setForgotError('');
    setForgotSuccess('');

    if (newPassword !== confirmPassword) {
      setForgotError('كلمات المرور غير متطابقة');
      return;
    }

    setForgotLoading(true);
    try {
      await axiosInstance.post(api.auth.resetPassword, {
        email: forgotEmail,
        otp,
        newPassword,
      });
      setForgotSuccess('تم تغيير كلمة المرور بنجاح');
      setTimeout(() => {
        setShowForgotPassword(false);
        setForgotStep('email');
        setForgotEmail('');
        setOtp('');
        setNewPassword('');
        setConfirmPassword('');
      }, 2000);
    } catch (err: any) {
      setForgotError(err.response?.data?.error || 'حدث خطأ أثناء إعادة تعيين كلمة المرور');
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center"
      style={{
        background: 'var(--section-gradient)',
      }}
    >
      {toast && (
        <div
          className="fixed top-4 left-1/2 -translate-x-1/2 z-50 px-4 py-3 rounded-lg shadow-lg text-white text-center max-w-sm"
          style={{ background: 'var(--navbar-bg)' }}
          role="alert"
        >
          {toast.message}
        </div>
      )}
      <div className="w-full max-w-md p-8 bg-white rounded-lg shadow-lg">
        <div className="flex justify-center mb-6">
          <img
            src={`${process.env.PUBLIC_URL || ''}/logo.png`}
            alt="مجموعة كاف الدولية"
            className="h-20 w-auto object-contain"
          />
        </div>
        <h1 className="text-center mb-8 text-2xl font-semibold" style={{ color: 'var(--navbar-bg)' }}>
          تسجيل الدخول
        </h1>
        {error && (
          <div className="p-3 mb-4 bg-red-50 text-red-700 rounded-md border border-red-200">
            {error}
          </div>
        )}
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block mb-2" style={{ color: 'var(--text-primary)' }}>
              البريد الإلكتروني
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-opacity-50"
              style={{
                borderColor: 'var(--border-main)',
                '--tw-ring-color': 'var(--navbar-border)',
              } as React.CSSProperties}
            />
          </div>
          <div className="mb-4">
            <label className="block mb-2" style={{ color: 'var(--text-primary)' }}>
              كلمة المرور
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full px-3 py-2 pr-10 border rounded-md focus:outline-none focus:ring-2 focus:ring-opacity-50"
                style={{
                  borderColor: 'var(--border-main)',
                  '--tw-ring-color': 'var(--navbar-border)',
                } as React.CSSProperties}
              />
              <button
                type="button"
                onClick={() => setShowPassword((p) => !p)}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-gray-500 hover:text-gray-700 focus:outline-none"
                aria-label={showPassword ? 'إخفاء كلمة المرور' : 'إظهار كلمة المرور'}
              >
                {showPassword ? (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                    <path d="M3.98 8.88A11.45 11.45 0 0 1 12 7c3.21 0 6.16 1.6 8.02 4.12a.75.75 0 0 1 0 .76A11.45 11.45 0 0 1 12 17c-3.21 0-6.16-1.6-8.02-4.12a.75.75 0 0 1 0-.76zM12 15.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7z" />
                    <path d="M2.22 2.22a.75.75 0 0 1 1.06 0l18 18a.75.75 0 1 1-1.06 1.06L2.22 3.28a.75.75 0 0 1 0-1.06z" />
                  </svg>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                    <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17a5 5 0 1 1 0-10 5 5 0 0 1 0 10zm0-8a3 3 0 1 0 0 6 3 3 0 0 0 0-6z" />
                  </svg>
                )}
              </button>
            </div>
          </div>
          <div className="mb-6 text-right">
            <button
              type="button"
              onClick={() => setShowForgotPassword(true)}
              className="text-sm"
              style={{ color: 'var(--navbar-border)' }}
            >
              نسيت كلمة المرور؟
            </button>
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 rounded-md text-lg font-semibold transition-opacity hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed"
            style={{
              background: 'var(--button-gradient)',
              color: 'var(--button-text)',
            }}
          >
            {loading ? 'جاري تسجيل الدخول...' : 'تسجيل الدخول'}
          </button>
        </form>

        {showForgotPassword && (
          <div className="mt-6 pt-6 border-t">
            <h2 className="text-xl font-semibold mb-4 text-center" style={{ color: 'var(--navbar-bg)' }}>
              إعادة تعيين كلمة المرور
            </h2>
            {forgotError && (
              <div className="p-3 mb-4 bg-red-50 text-red-700 rounded-md border border-red-200">
                {forgotError}
              </div>
            )}
            {forgotSuccess && (
              <div className="p-3 mb-4 bg-green-50 text-green-700 rounded-md border border-green-200">
                {forgotSuccess}
              </div>
            )}

            {forgotStep === 'email' && (
              <form onSubmit={handleForgotPasswordEmail}>
                <div className="mb-4">
                  <label className="block mb-2" style={{ color: 'var(--text-primary)' }}>
                    البريد الإلكتروني
                  </label>
                  <input
                    type="email"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    required
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-opacity-50"
                    style={{
                      borderColor: 'var(--border-main)',
                      '--tw-ring-color': 'var(--navbar-border)',
                    } as React.CSSProperties}
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    type="submit"
                    disabled={forgotLoading}
                    className="flex-1 py-2 rounded-md font-semibold transition-opacity hover:opacity-90 disabled:opacity-60"
                    style={{
                      background: 'var(--button-gradient)',
                      color: 'var(--button-text)',
                    }}
                  >
                    {forgotLoading ? 'جاري الإرسال...' : 'إرسال رمز التحقق'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowForgotPassword(false);
                      setForgotStep('email');
                      setForgotError('');
                      setForgotSuccess('');
                    }}
                    className="px-4 py-2 rounded-md border transition-opacity hover:opacity-90"
                    style={{
                      borderColor: 'var(--border-main)',
                      color: 'var(--text-primary)',
                    }}
                  >
                    إلغاء
                  </button>
                </div>
              </form>
            )}

            {forgotStep === 'otp' && (
              <form onSubmit={handleResetPassword}>
                <div className="mb-4">
                  <label className="block mb-2" style={{ color: 'var(--text-primary)' }}>
                    رمز التحقق
                  </label>
                  <input
                    type="text"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    required
                    maxLength={6}
                    placeholder="أدخل الرمز المكون من 6 أرقام"
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-opacity-50 text-center text-2xl tracking-widest"
                    style={{
                      borderColor: 'var(--border-main)',
                      '--tw-ring-color': 'var(--navbar-border)',
                    } as React.CSSProperties}
                  />
                </div>
                <div className="mb-4">
                  <label className="block mb-2" style={{ color: 'var(--text-primary)' }}>
                    كلمة المرور الجديدة
                  </label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={6}
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-opacity-50"
                    style={{
                      borderColor: 'var(--border-main)',
                      '--tw-ring-color': 'var(--navbar-border)',
                    } as React.CSSProperties}
                  />
                </div>
                <div className="mb-4">
                  <label className="block mb-2" style={{ color: 'var(--text-primary)' }}>
                    تأكيد كلمة المرور
                  </label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    minLength={6}
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-opacity-50"
                    style={{
                      borderColor: 'var(--border-main)',
                      '--tw-ring-color': 'var(--navbar-border)',
                    } as React.CSSProperties}
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    type="submit"
                    disabled={forgotLoading}
                    className="flex-1 py-2 rounded-md font-semibold transition-opacity hover:opacity-90 disabled:opacity-60"
                    style={{
                      background: 'var(--button-gradient)',
                      color: 'var(--button-text)',
                    }}
                  >
                    {forgotLoading ? 'جاري التغيير...' : 'تغيير كلمة المرور'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowForgotPassword(false);
                      setForgotStep('email');
                      setForgotError('');
                      setForgotSuccess('');
                    }}
                    className="px-4 py-2 rounded-md border transition-opacity hover:opacity-90"
                    style={{
                      borderColor: 'var(--border-main)',
                      color: 'var(--text-primary)',
                    }}
                  >
                    إلغاء
                  </button>
                </div>
              </form>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Login;
